/**
 * What an assistant actually earns from a booking.
 *
 * The API never sends a plain "amount" on a booking — it sends the payout
 * breakdown it snapshotted when the traveller paid. Earnings are the
 * assistant's net fee (her fee less the platform commission) plus the add-on
 * money, which is pass-through logistics cash and carries no commission.
 *
 * This mirrors the payout the API books into her wallet (computeBookingPayout /
 * releaseBookingPayout in pulo_api), so a total built from these numbers
 * reconciles with available + ledger (locked) balance on her profile.
 */

const PLATFORM_COMMISSION_RATE = 0.2;

const roundToTwo = (value) => Math.round(value * 100) / 100;

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Gross value of the booking — what the traveller paid, not what she keeps. */
export const getBookingGrossAmount = (booking = {}) =>
  toNumber(
    booking?.amountPaid ??
      booking?.amountExpected ??
      booking?.amount ??
      booking?.totalAmount ??
      booking?.total_amount ??
      booking?.price
  );

export const getAssistantBookingEarnings = (booking = {}) => {
  const amountPaid = toNumber(booking?.amountPaid ?? booking?.amountExpected);
  if (amountPaid <= 0) return 0;

  const addOnsTotal = Math.min(toNumber(booking?.addOnsTotal), amountPaid);

  // Split-payout bookings carry the breakdown the API computed at payment.
  // Prefer the stored figure over recomputing it: the commission rate can
  // change, and the snapshot is what she was actually promised.
  const storedNetFee = toNumber(booking?.assistantNetFee);
  if (storedNetFee > 0) return roundToTwo(storedNetFee + addOnsTotal);

  if (toNumber(booking?.payoutVersion) >= 2) {
    const assistantFee = roundToTwo(amountPaid - addOnsTotal);
    const netFee = roundToTwo(assistantFee - assistantFee * PLATFORM_COMMISSION_RATE);
    return roundToTwo(netFee + addOnsTotal);
  }

  // Pre-split booking: commission was charged on the whole amount, and the
  // remainder is hers. Fall back to the standard rate only if the snapshot is
  // missing, which is how the API values these rows too.
  const commission =
    booking?.platformCommissionAmount != null
      ? toNumber(booking.platformCommissionAmount)
      : roundToTwo(amountPaid * PLATFORM_COMMISSION_RATE);

  return Math.max(0, roundToTwo(amountPaid - commission));
};
